import { RefObject, useCallback, useMemo, useState } from 'react';
import { useResizeObserver } from '@/hooks';

/** The height the textarea may grow to when the column has room to spare. */
export const DEFAULT_MAX_TEXTAREA_HEIGHT_PX = 285;

/** Mirrors `min-h-[44px]` on the textarea - below this the box only clips its own text. */
export const MIN_TEXTAREA_HEIGHT_PX = 44;

/** Max share of the chat column for the input dock, so the transcript stays scrollable. */
const MAX_DOCK_HEIGHT_FRACTION = 0.5;

/** Marks the column the transcript and the input dock share. */
export const CHAT_COLUMN_ATTR = 'data-chat-column';

/** Marks the block the input is docked in, disclaimer and padding included. */
export const CHAT_INPUT_DOCK_ATTR = 'data-chat-input-dock';

/** Per-dock override of the height share, e.g. `data-dock-height-fraction="0.95"`. For pages without a transcript. */
export const CHAT_INPUT_DOCK_HEIGHT_FRACTION_ATTR = 'data-dock-height-fraction';

/**
 * Per-dock override: cap the textarea at `window.innerHeight - offsetPx`, e.g.
 * `data-dock-height-offset="240"`. Wins over the fraction.
 */
export const CHAT_INPUT_DOCK_HEIGHT_OFFSET_ATTR = 'data-dock-height-offset';

type UseChatInputMaxHeightOptions = {
  /** The bordered input box, used to find the dock and column it sits in. */
  containerElement: HTMLElement | null;
  textareaRef: RefObject<HTMLTextAreaElement | null>;
};

/**
 * Caps the chat textarea height so the transcript keeps enough room. Without a transcript the plain maximum applies.
 */
export function useChatInputMaxHeight({ containerElement, textareaRef }: UseChatInputMaxHeightOptions): number {
  const [maxHeight, setMaxHeight] = useState(DEFAULT_MAX_TEXTAREA_HEIGHT_PX);

  const dock = useMemo(
    () => containerElement?.closest<HTMLElement>(`[${CHAT_INPUT_DOCK_ATTR}]`) ?? null,
    [containerElement],
  );
  const column = useMemo(
    () => containerElement?.closest<HTMLElement>(`[${CHAT_COLUMN_ATTR}]`) ?? null,
    [containerElement],
  );

  /**
   * Runs off the observers below rather than an effect: `ResizeObserver` reports the
   * element's size as soon as it starts observing, which covers the first measurement too.
   */
  const measure = useCallback(() => {
    const textarea = textareaRef.current;

    if (!textarea || !dock || !column) {
      setMaxHeight(DEFAULT_MAX_TEXTAREA_HEIGHT_PX);
      return;
    }

    /** Height of everything in the dock except the textarea. */
    const chromeHeight = dock.scrollHeight - textarea.offsetHeight;

    // Absolute pixel-offset mode wins over fraction mode when both are set.
    const offsetRaw = Number(dock.getAttribute(CHAT_INPUT_DOCK_HEIGHT_OFFSET_ATTR));
    const hasExplicitOffset = Number.isFinite(offsetRaw) && offsetRaw > 0;
    if (hasExplicitOffset) {
      const budgetFromOffset = window.innerHeight - offsetRaw - chromeHeight;
      setMaxHeight(Math.round(Math.max(MIN_TEXTAREA_HEIGHT_PX, budgetFromOffset)));
      return;
    }

    const dockFractionRaw = Number(dock.getAttribute(CHAT_INPUT_DOCK_HEIGHT_FRACTION_ATTR));
    const hasExplicitFraction = Number.isFinite(dockFractionRaw) && dockFractionRaw > 0;
    const dockFraction = hasExplicitFraction ? dockFractionRaw : MAX_DOCK_HEIGHT_FRACTION;
    const budget = column.clientHeight * dockFraction - chromeHeight;
    // With an explicit fraction, drop the default cap — the fraction bounds the height.
    const maxCap = hasExplicitFraction ? Number.POSITIVE_INFINITY : DEFAULT_MAX_TEXTAREA_HEIGHT_PX;

    setMaxHeight(Math.round(Math.min(maxCap, Math.max(MIN_TEXTAREA_HEIGHT_PX, budget))));
  }, [column, dock, textareaRef]);

  // The column tracks the viewport; the dock grows and shrinks as chips and clarifiers come and go.
  useResizeObserver(column, undefined, measure);
  useResizeObserver(dock, undefined, measure);

  return maxHeight;
}
